import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function OvertimePage() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = status === "all" ? {} : { status };
      const { data } = await api.get("/overtime", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post("/overtime/sync"); toast.success(`Synced: ${data.created} new`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const decide = async (id, decision) => {
    try { await api.post(`/overtime/${id}/decision`, { decision }); toast.success(`Marked ${decision}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="overtime-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Exceptions</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Overtime</h1>
          <p className="text-muted-foreground mt-1">Review and approve hours worked above the weekly standard.</p>
        </div>
        <div className="flex items-end gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="ot-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={sync} disabled={busy} data-testid="ot-sync">{busy ? "Syncing…" : "Sync from time entries"}</Button>
        </div>
      </div>

      <Card className="border-border shadow-none">
        <div className="overflow-x-auto app-scroll">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left tiny-label">
                <th className="px-4 py-3">Week</th>
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3 text-right">Worked</th>
                <th className="px-4 py-3 text-right">Standard</th>
                <th className="px-4 py-3 text-right">Overtime</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`ot-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono">{r.week_start}</td>
                  <td className="px-4 py-3">{r.employee?.full_name || r.employee_id}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.total_hours.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono text-muted-foreground">{r.standard_hours.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-700 font-semibold">{r.overtime_hours.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={
                      r.status === "approved" ? "text-emerald-700 border-emerald-200 bg-emerald-50" :
                      r.status === "rejected" ? "text-red-700 border-red-200 bg-red-50" :
                      "text-amber-700 border-amber-200 bg-amber-50"
                    }>{r.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.status === "pending" && (
                      <div className="inline-flex gap-2">
                        <Button size="sm" onClick={() => decide(r.id, "approved")} data-testid={`ot-approve-${r.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => decide(r.id, "rejected")} data-testid={`ot-reject-${r.id}`}>Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No overtime records. Click "Sync from time entries" to refresh.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
